/*
 * Lecturas y escrituras con TanStack Query.
 *
 * Cada lectura tiene una llave (`claves`) y cada escritura invalida las llaves que
 * toca, así la pantalla se refresca sola después de guardar. Las escrituras
 * optimistas (verificar hallazgos) viven en la pantalla que las necesita, porque
 * ahí está el estado local que hay que adelantar.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./cliente";
import type {
  Cuenta, CuentaDetalle, EstadoTarea, Hallazgo, Hoy, InicioDescubrimiento, Lectura, NuevaCuenta, NuevaInteraccion,
  NuevaEntrada, NuevaPersona, NuevaPropuesta, NuevaSesion, NuevoHallazgo, Oportunidad, Pago, PasoProceso, Persona,
  PrimerEncargo, Propuesta, Proyecto, Puntajes, Sesion, Tarea, Transicion, Vista, Zona, EstadoZona,
  ContextoExploracion, ConversacionExploracion, IdeaExplorada, NuevoMensaje, ResumenConversacion, VistaPreviaPrompt,
} from "./tipos";

export const claves = {
  hoy: ["hoy"] as const,
  cuentas: ["cuentas"] as const,
  cuenta: (id: string) => ["cuenta", id] as const,
  vista: (caso: string) => ["vista", caso] as const,
  personas: (caso: string) => ["personas", caso] as const,
  sesiones: (caso: string) => ["sesiones", caso] as const,
  candidatos: (caso: string) => ["candidatos", caso] as const,
  verificados: (caso: string) => ["verificados", caso] as const,
  transiciones: (caso: string) => ["transiciones", caso] as const,
};

// ─── Cuentas y Hoy ──────────────────────────────────────────────────────────

export const useHoy = () => useQuery({ queryKey: claves.hoy, queryFn: () => api<Hoy>("/api/hoy") });
export const useCuentas = () => useQuery({ queryKey: claves.cuentas, queryFn: () => api<Cuenta[]>("/api/cuentas") });
export const useCuenta = (id: string) =>
  useQuery({ queryKey: claves.cuenta(id), queryFn: () => api<CuentaDetalle>(`/api/cuentas/${id}`) });

export function useCrearCuenta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datos: NuevaCuenta) => api<Cuenta>("/api/cuentas", { body: datos }),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.cuentas }),
  });
}

export function useRegistrarInteraccion(cuentaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datos: NuevaInteraccion) => api(`/api/cuentas/${cuentaId}/interacciones`, { body: datos }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: claves.cuenta(cuentaId) });
      qc.invalidateQueries({ queryKey: claves.cuentas });
      qc.invalidateQueries({ queryKey: claves.hoy });
    },
  });
}

export function useEditarCuenta(cuentaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datos: Partial<Pick<Cuenta, "proxima_accion" | "compromiso_fecha" | "notas" | "fit" | "prioridad" | "contacto" | "rol">> & { fria?: boolean }) =>
      api<Cuenta>(`/api/cuentas/${cuentaId}/editar`, { body: datos }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: claves.cuenta(cuentaId) });
      qc.invalidateQueries({ queryKey: claves.cuentas });
      qc.invalidateQueries({ queryKey: claves.hoy });
    },
  });
}

export function useIniciarDescubrimiento(cuentaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datos: InicioDescubrimiento) =>
      api<{ caso_id: string; estado: string }>(`/api/cuentas/${cuentaId}/descubrimiento`, { body: datos }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: claves.cuenta(cuentaId) });
      qc.invalidateQueries({ queryKey: claves.cuentas });
      qc.invalidateQueries({ queryKey: claves.hoy });
    },
  });
}

// ─── Expediente ─────────────────────────────────────────────────────────────

const caso = (id: string) => `/api/casos/${encodeURIComponent(id)}`;

export const useVista = (casoId: string | null) =>
  useQuery({ queryKey: claves.vista(casoId ?? ""), queryFn: () => api<Vista>(caso(casoId!)), enabled: !!casoId });
export const usePersonas = (casoId: string | null) =>
  useQuery({ queryKey: claves.personas(casoId ?? ""), queryFn: () => api<Persona[]>(`${caso(casoId!)}/personas`), enabled: !!casoId });
export const useSesiones = (casoId: string | null) =>
  useQuery({ queryKey: claves.sesiones(casoId ?? ""), queryFn: () => api<Sesion[]>(`${caso(casoId!)}/sesiones`), enabled: !!casoId });
export const useCandidatos = (casoId: string | null) =>
  useQuery({ queryKey: claves.candidatos(casoId ?? ""), queryFn: () => api<Hallazgo[]>(`${caso(casoId!)}/hallazgos/candidatos`), enabled: !!casoId });
export const useVerificados = (casoId: string | null) =>
  useQuery({ queryKey: claves.verificados(casoId ?? ""), queryFn: () => api<Hallazgo[]>(`${caso(casoId!)}/hallazgos/verificados`), enabled: !!casoId });
export const useTransiciones = (casoId: string | null) =>
  useQuery({ queryKey: claves.transiciones(casoId ?? ""), queryFn: () => api<Transicion[]>(`${caso(casoId!)}/transiciones`), enabled: !!casoId });

/** Invalida todo lo que depende de un expediente. Se llama tras cualquier escritura sobre él. */
export function useInvalidarCaso(casoId: string, cuentaId?: string) {
  const qc = useQueryClient();
  return () => {
    for (const k of [claves.vista, claves.personas, claves.sesiones, claves.candidatos, claves.verificados, claves.transiciones]) {
      qc.invalidateQueries({ queryKey: k(casoId) });
    }
    qc.invalidateQueries({ queryKey: claves.hoy });
    qc.invalidateQueries({ queryKey: claves.cuentas });
    if (cuentaId) qc.invalidateQueries({ queryKey: claves.cuenta(cuentaId) });
  };
}

export function useAgregarSesion(casoId: string, cuentaId?: string) {
  const invalidar = useInvalidarCaso(casoId, cuentaId);
  return useMutation({
    mutationFn: (datos: NuevaSesion) => api<Sesion>(`${caso(casoId)}/sesiones`, { body: datos }),
    onSuccess: invalidar,
  });
}

export function useAgregarPersona(casoId: string, cuentaId?: string) {
  const invalidar = useInvalidarCaso(casoId, cuentaId);
  return useMutation({
    mutationFn: (datos: NuevaPersona) => api<Persona>(`${caso(casoId)}/personas`, { body: datos }),
    onSuccess: invalidar,
  });
}

export function useAgregarEntrada(casoId: string, cuentaId?: string) {
  const invalidar = useInvalidarCaso(casoId, cuentaId);
  return useMutation({
    mutationFn: ({ sesionId, ...datos }: NuevaEntrada & { sesionId: string }) =>
      api(`${caso(casoId)}/sesiones/${encodeURIComponent(sesionId)}/entradas`, { body: datos }),
    onSuccess: invalidar,
  });
}

export function useProponerHallazgo(casoId: string, cuentaId?: string) {
  const invalidar = useInvalidarCaso(casoId, cuentaId);
  return useMutation({
    mutationFn: (datos: NuevoHallazgo) => api<Hallazgo>(`${caso(casoId)}/hallazgos`, { body: datos }),
    onSuccess: invalidar,
  });
}

export function useAplicarTransicion(casoId: string, cuentaId?: string) {
  const invalidar = useInvalidarCaso(casoId, cuentaId);
  return useMutation({
    mutationFn: (datos: { hacia: string; actor_id: string; overrides?: { condicion_id: string; motivo: string }[] }) =>
      api<{ estado: string }>(`${caso(casoId)}/transiciones/aplicar`, {
        body: { ...datos, transicion_id: `t-${Date.now().toString(36)}`, overrides: datos.overrides ?? [] },
      }),
    onSettled: invalidar, // también si falla: la transición rechazada queda en el log
  });
}

// ─── Comercial ──────────────────────────────────────────────────────────────

export const clavesComercial = {
  proyectos: ["proyectos"] as const,
  proyecto: (id: string) => ["proyecto", id] as const,
  pagos: ["pagos"] as const,
};

export const useProyectos = () => useQuery({ queryKey: clavesComercial.proyectos, queryFn: () => api<Proyecto[]>("/api/proyectos") });
export const useProyecto = (id: string) => useQuery({ queryKey: clavesComercial.proyecto(id), queryFn: () => api<Proyecto>(`/api/proyectos/${id}`) });
export const usePagos = () => useQuery({ queryKey: clavesComercial.pagos, queryFn: () => api<Pago[]>("/api/pagos") });

/** Cualquier escritura comercial toca la cuenta, los listados del rail y Hoy. */
function useInvalidarComercial(cuentaId: string, proyectoId?: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: claves.cuenta(cuentaId) });
    qc.invalidateQueries({ queryKey: claves.cuentas });
    qc.invalidateQueries({ queryKey: claves.hoy });
    qc.invalidateQueries({ queryKey: clavesComercial.proyectos });
    qc.invalidateQueries({ queryKey: clavesComercial.pagos });
    if (proyectoId) qc.invalidateQueries({ queryKey: clavesComercial.proyecto(proyectoId) });
  };
}

export function useCrearPropuesta(cuentaId: string) {
  const invalidar = useInvalidarComercial(cuentaId);
  return useMutation({ mutationFn: (datos: NuevaPropuesta) => api<Propuesta>(`/api/cuentas/${cuentaId}/propuestas`, { body: datos }), onSuccess: invalidar });
}

export function useCambiarPropuesta(cuentaId: string) {
  const invalidar = useInvalidarComercial(cuentaId);
  return useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: "enviada" | "aceptada" | "rechazada" }) =>
      api<{ propuesta: Propuesta; proyecto_id: string | null }>(`/api/cuentas/${cuentaId}/propuestas/${id}/estado`, { body: { estado } }),
    onSuccess: invalidar,
  });
}

export function useArrancarProyecto(cuentaId: string, pid: string) {
  const invalidar = useInvalidarComercial(cuentaId, pid);
  return useMutation({ mutationFn: (datos: { inicio: string; comercial_id: string }) => api<Proyecto>(`/api/cuentas/${cuentaId}/proyectos/${pid}/arrancar`, { body: datos }), onSuccess: invalidar });
}

export function useNuevaTarea(cuentaId: string, pid: string) {
  const invalidar = useInvalidarComercial(cuentaId, pid);
  return useMutation({ mutationFn: (datos: { fase: number; titulo: string; quien: string; hito?: boolean }) => api<Tarea>(`/api/cuentas/${cuentaId}/proyectos/${pid}/tareas`, { body: datos }), onSuccess: invalidar });
}

export function useCambiarTarea(cuentaId: string, pid: string) {
  const qc = useQueryClient();
  const invalidar = useInvalidarComercial(cuentaId, pid);
  return useMutation({
    mutationFn: ({ tid, ...datos }: { tid: string; estado: EstadoTarea; bloqueo?: string | null }) =>
      api<{ tarea: Tarea; fase: number }>(`/api/cuentas/${cuentaId}/proyectos/${pid}/tareas/${tid}`, { body: datos }),
    // Optimista: la tarjeta cambia de columna al soltarla; el servidor confirma detrás.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: clavesComercial.proyecto(pid) });
      const antes = qc.getQueryData<Proyecto>(clavesComercial.proyecto(pid));
      if (antes) qc.setQueryData<Proyecto>(clavesComercial.proyecto(pid), { ...antes, tareas: antes.tareas.map((t) => (t.id === v.tid ? { ...t, estado: v.estado, bloqueo: v.estado === "bloqueado" ? (v.bloqueo ?? t.bloqueo) : null } : t)) });
      return { antes };
    },
    onError: (_e, _v, ctx) => { if (ctx?.antes) qc.setQueryData(clavesComercial.proyecto(pid), ctx.antes); },
    onSettled: invalidar,
  });
}

export function useNuevaLectura(cuentaId: string, pid: string) {
  const invalidar = useInvalidarComercial(cuentaId, pid);
  return useMutation({ mutationFn: (datos: { fecha: string; valor: number }) => api<Lectura>(`/api/cuentas/${cuentaId}/proyectos/${pid}/lecturas`, { body: datos }), onSuccess: invalidar });
}

export function useDependencia(cuentaId: string, pid: string) {
  const invalidar = useInvalidarComercial(cuentaId, pid);
  return useMutation({ mutationFn: (datos: { texto: string; quien: string; resolver?: boolean }) => api<Proyecto>(`/api/cuentas/${cuentaId}/proyectos/${pid}/dependencias`, { body: datos }), onSuccess: invalidar });
}

export function useCerrarProyecto(cuentaId: string, pid: string) {
  const invalidar = useInvalidarComercial(cuentaId, pid);
  return useMutation({ mutationFn: () => api<Proyecto>(`/api/cuentas/${cuentaId}/proyectos/${pid}/cerrar`, { body: {} }), onSuccess: invalidar });
}

export function useNuevoPago(cuentaId: string) {
  const invalidar = useInvalidarComercial(cuentaId);
  return useMutation({ mutationFn: (datos: { concepto: string; monto: number; vence: string; proyecto_id?: string | null; proyecto_nombre?: string; condicionado?: boolean; facturar?: boolean }) => api<Pago>(`/api/cuentas/${cuentaId}/pagos`, { body: datos }), onSuccess: invalidar });
}

export function useAccionPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cuentaId, id, accion }: { cuentaId: string; id: string; accion: "facturar" | "cobrar" }) =>
      api<Pago>(`/api/cuentas/${cuentaId}/pagos/${id}`, { body: { accion } }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: claves.cuenta(v.cuentaId) });
      qc.invalidateQueries({ queryKey: clavesComercial.pagos });
      qc.invalidateQueries({ queryKey: claves.hoy });
    },
  });
}

// ─── El embudo: zonas, cartera, primer encargo y cierre ─────────────────────

export const clavesEmbudo = {
  zonas: (caso: string) => ["zonas", caso] as const,
  oportunidades: (caso: string) => ["oportunidades", caso] as const,
  primerEncargo: (caso: string) => ["primer-encargo", caso] as const,
};

export const useZonas = (casoId: string | null) =>
  useQuery({ queryKey: clavesEmbudo.zonas(casoId ?? ""), queryFn: () => api<Zona[]>(`${caso(casoId!)}/zonas`), enabled: !!casoId });
export const useOportunidades = (casoId: string | null) =>
  useQuery({ queryKey: clavesEmbudo.oportunidades(casoId ?? ""), queryFn: () => api<Oportunidad[]>(`${caso(casoId!)}/oportunidades`), enabled: !!casoId });
export const usePrimerEncargo = (casoId: string | null) =>
  useQuery({ queryKey: clavesEmbudo.primerEncargo(casoId ?? ""), queryFn: () => api<PrimerEncargo>(`${caso(casoId!)}/primer-encargo`), enabled: !!casoId });

/** Todo lo del embudo mueve las mismas vistas: el caso entero más los tres listados. */
function useInvalidarEmbudo(casoId: string, cuentaId?: string) {
  const qc = useQueryClient();
  const invalidarCaso = useInvalidarCaso(casoId, cuentaId);
  return () => {
    invalidarCaso();
    for (const k of [clavesEmbudo.zonas, clavesEmbudo.oportunidades, clavesEmbudo.primerEncargo]) {
      qc.invalidateQueries({ queryKey: k(casoId) });
    }
  };
}

/** Una sola fábrica: todas las escrituras del embudo son POST + invalidar lo mismo. */
function useAccionEmbudo<T>(casoId: string, cuentaId: string | undefined, ruta: (v: T) => string, cuerpo: (v: T) => unknown) {
  const invalidar = useInvalidarEmbudo(casoId, cuentaId);
  return useMutation({ mutationFn: (v: T) => api(`${caso(casoId)}${ruta(v)}`, { body: cuerpo(v) }), onSuccess: invalidar });
}

export const useCrearZona = (c: string, cu?: string) =>
  useAccionEmbudo<{ id: string; nombre: string; hallazgos_evidencia: string[] }>(c, cu, () => "/zonas", (v) => v);

export const useEstadoZona = (c: string, cu?: string) =>
  useAccionEmbudo<{ zonaId: string; estado: EstadoZona; motivo_descarte?: string | null }>(
    c, cu, (v) => `/zonas/${v.zonaId}/estado`, (v) => ({ estado: v.estado, motivo_descarte: v.motivo_descarte ?? null }));

export const useRegistrarProceso = (c: string, cu?: string) =>
  useAccionEmbudo<{ zonaId: string; origen: "declarado" | "observado"; sesion_id: string; pasos: PasoProceso[] }>(
    c, cu, (v) => `/zonas/${v.zonaId}/proceso`, (v) => ({ origen: v.origen, sesion_id: v.sesion_id, pasos: v.pasos }));

export const useDelta = (c: string, cu?: string) =>
  useAccionEmbudo<{ zonaId: string; confirmar: boolean; confirmador_id?: string }>(
    c, cu, (v) => `/zonas/${v.zonaId}/delta/${v.confirmar ? "confirmar" : "calcular"}`, (v) => (v.confirmar ? { confirmador_id: v.confirmador_id } : {}));

export const useCrearOportunidad = (c: string, cu?: string) =>
  useAccionEmbudo<{ id: string; titulo: string; descripcion_una_linea: string; zona_caliente_id: string; hallazgos_ancla: string[] }>(
    c, cu, () => "/oportunidades", (v) => v);

export const useAbrirEvaluacion = (c: string, cu?: string) =>
  useAccionEmbudo<{ oportunidadId: string; sesion_id: string; evaluacion_id: string }>(
    c, cu, (v) => `/oportunidades/${v.oportunidadId}/evaluacion/abrir`, (v) => ({ sesion_id: v.sesion_id, evaluacion_id: v.evaluacion_id }));

export const useVotar = (c: string, cu?: string) =>
  useAccionEmbudo<{ oportunidadId: string; evaluador_id: string } & Puntajes>(
    c, cu, (v) => `/oportunidades/${v.oportunidadId}/evaluacion/voto`,
    (v) => ({ evaluador_id: v.evaluador_id, impacto: v.impacto, tiempo: v.tiempo, factibilidad: v.factibilidad, baseline: v.baseline, respeto: v.respeto }));

export const useConsenso = (c: string, cu?: string) =>
  useAccionEmbudo<{ oportunidadId: string; participantes: string[] } & Puntajes>(
    c, cu, (v) => `/oportunidades/${v.oportunidadId}/evaluacion/consenso`,
    (v) => ({ participantes: v.participantes, impacto: v.impacto, tiempo: v.tiempo, factibilidad: v.factibilidad, baseline: v.baseline, respeto: v.respeto }));

export const useCongelarRanking = (c: string, cu?: string) => useAccionEmbudo<void>(c, cu, () => "/ranking/congelar", () => ({}));

export const useAbrirPrimerEncargo = (c: string, cu?: string) =>
  useAccionEmbudo<{ oportunidad_id: string; id: string }>(c, cu, () => "/primer-encargo/abrir", (v) => v);

export const useValidarCompuerta = (c: string, cu?: string) =>
  useAccionEmbudo<{ clave: string; cumple: boolean; evidencia_hallazgo_ids: string[]; justificacion: string; validado_por: string }>(
    c, cu, (v) => `/primer-encargo/compuertas/${v.clave}`,
    (v) => ({ cumple: v.cumple, evidencia_hallazgo_ids: v.evidencia_hallazgo_ids, justificacion: v.justificacion, validado_por: v.validado_por }));

export const usePonerArtefacto = (c: string, cu?: string) =>
  useAccionEmbudo<{ id: string; tipo: "lo_que_escuchamos" | "hoja_de_un_solo_numero"; contenido: Record<string, unknown> }>(c, cu, () => "/artefactos", (v) => v);

export const useConfirmarGobernanza = (c: string, cu?: string) =>
  useAccionEmbudo<{ personaId: string; hallazgo_id: string }>(c, cu, (v) => `/personas/${v.personaId}/gobernanza`, (v) => ({ hallazgo_id: v.hallazgo_id }));

export const usePonerBaseline = (c: string, cu?: string) =>
  useAccionEmbudo<Record<string, unknown>>(c, cu, () => "/baseline", (v) => v);
export const usePonerIndicador = (c: string, cu?: string) =>
  useAccionEmbudo<Record<string, unknown>>(c, cu, () => "/indicador", (v) => v);
export const usePonerTerminos = (c: string, cu?: string) =>
  useAccionEmbudo<Record<string, unknown>>(c, cu, () => "/terminos", (v) => v);

// ─── Playground: conversaciones con la IA ───────────────────────────────────

export const clavesPlayground = {
  conversaciones: (caso: string) => ["playground", caso, "lista"] as const,
  conversacion: (caso: string, id: string) => ["playground", caso, "conv", id] as const,
  todas: (caso: string) => ["playground", caso] as const,
  contexto: (caso: string) => ["playground-contexto", caso] as const,
};
const pg = (casoId: string) => `${caso(casoId)}/playground`;

export const useContextoPlayground = (casoId: string) =>
  useQuery({ queryKey: clavesPlayground.contexto(casoId), queryFn: () => api<ContextoExploracion>(`${pg(casoId)}/contexto`), enabled: !!casoId });
export const useConversaciones = (casoId: string) =>
  useQuery({ queryKey: clavesPlayground.conversaciones(casoId), queryFn: () => api<ResumenConversacion[]>(`${pg(casoId)}/conversaciones`), enabled: !!casoId });
export const useConversacion = (casoId: string, id: string | null) =>
  useQuery({
    queryKey: clavesPlayground.conversacion(casoId, id ?? ""),
    queryFn: () => api<ConversacionExploracion>(`${pg(casoId)}/conversaciones/${encodeURIComponent(id!)}`),
    enabled: !!casoId && !!id,
  });

/** Manda un mensaje; la respuesta es la conversación completa, que se guarda tal cual en caché. */
export function useMandarMensaje(casoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datos: NuevoMensaje) => api<ConversacionExploracion>(`${pg(casoId)}/mensajes`, { body: datos }),
    onSuccess: (c) => {
      qc.setQueryData(clavesPlayground.conversacion(casoId, c.id), c);
      qc.invalidateQueries({ queryKey: clavesPlayground.conversaciones(casoId) });
    },
  });
}

/** Lo que se le mandaría a la IA con este mensaje, sin mandarlo. */
export const useVistaPrevia = (casoId: string) =>
  useMutation({ mutationFn: (datos: NuevoMensaje) => api<VistaPreviaPrompt>(`${pg(casoId)}/vista-previa`, { body: datos }) });

export function useRenombrarConversacion(casoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; titulo: string }) => api(`${pg(casoId)}/conversaciones/${encodeURIComponent(v.id)}/titulo`, { body: { titulo: v.titulo } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: clavesPlayground.todas(casoId) }),
  });
}

export function useMarcarIdea(casoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ideaId: string; estado: "nueva" | "guardada" | "descartada"; motivo?: string | null }) =>
      api<IdeaExplorada>(`${pg(casoId)}/ideas/${encodeURIComponent(v.ideaId)}/estado`, { body: { estado: v.estado, motivo: v.motivo ?? null } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: clavesPlayground.todas(casoId) }),
  });
}

/** Convertir crea una ficha: además de la conversación, se mueve todo el embudo. */
export function useConvertirIdea(casoId: string, cuentaId: string | undefined) {
  const qc = useQueryClient();
  const invalidarEmbudo = useInvalidarEmbudo(casoId, cuentaId);
  return useMutation({
    mutationFn: (v: { ideaId: string; oportunidad_id: string; titulo: string; descripcion_una_linea: string; hallazgos_ancla: string[]; zona_caliente_id: string }) =>
      api<Oportunidad>(`${pg(casoId)}/ideas/${encodeURIComponent(v.ideaId)}/oportunidad`, {
        body: { oportunidad_id: v.oportunidad_id, titulo: v.titulo, descripcion_una_linea: v.descripcion_una_linea, hallazgos_ancla: v.hallazgos_ancla, zona_caliente_id: v.zona_caliente_id },
      }),
    onSuccess: () => { invalidarEmbudo(); qc.invalidateQueries({ queryKey: clavesPlayground.todas(casoId) }); },
  });
}
